import { Injectable } from '@angular/core';

import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, exhaustMap, map } from 'rxjs/operators';

import { EnvironmentService, EventService, GridService, ListService, fromList } from '@core/services';
import { currentTimeInSeconds } from '@util/list';

import { ListOfValuesResponse } from '#types/list';

import { CONSTANTS } from '@carrier/constants';
import { CarrierService } from '@carrier/services';

import * as CarrierActions from './carrier.actions';

@Injectable()
export class CarrierEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private carrierService: CarrierService,
    private gridService: GridService,
    private listService: ListService,
    private environmentService: EnvironmentService,
    private eventService: EventService
  ) {}

  RequestLists$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(CarrierActions.RequestLists),
        concatLatestFrom(() => [this.store.select(fromList.selectCarrierLists)]),
        map(([_, lists]) => {
          let lastUpdated = -1;

          for (const list of Object.keys(lists)) {
            // skip parameterized lists
            if (lists[list] && !Array.isArray(lists[list].data)) {
              continue;
            }

            if (lists[list]) {
              lastUpdated = Math.max(lastUpdated, currentTimeInSeconds() - (lists[list].updated ?? 0));
            }
          }

          if (lastUpdated === -1 || lastUpdated > this.environmentService.settings.secondsToCacheLists) {
            this.store.dispatch(CarrierActions.RequestCarrierList());
          }
        })
      );
    },
    { dispatch: false }
  );

  requestCarrierList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CarrierActions.RequestCarrierList),
      exhaustMap(() => {
        return this.listService.getListOfValues<ListOfValuesResponse>('carrier', 'carrier').pipe(
          this.eventService.indicate(CONSTANTS.LIST_ALL),
          map((list) => CarrierActions.RequestCarrierListSuccess({ list })),
          catchError((error) => of(CarrierActions.RequestCarrierListFailure(error)))
        );
      })
    );
  });

  requestCarrierDeliveryServiceCodeList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CarrierActions.RequestDeliveryServiceCodeList),
      exhaustMap((parameters) => {
        return this.listService
          .getListOfValuesWithParameters<ListOfValuesResponse>('carrier', 'deliveryServiceCode', { carrier: parameters.carrier })
          .pipe(
            this.eventService.indicate(CONSTANTS.LIST_DELIVERY_SERVICE_CODE),
            map((list) => CarrierActions.RequestDeliveryServiceCodeListSuccess({ carrier: parameters.carrier, list })),
            catchError((error) => of(CarrierActions.RequestDeliveryServiceCodeListFailure(error)))
          );
      })
    );
  });

  LookupRequestData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        CarrierActions.CarrierLookupDialogRequestData,
        CarrierActions.CarrierLookupDialogRequestDataWithFilters,
        CarrierActions.CarrierLookupDialogRequestDataWithParameters
      ),
      concatLatestFrom(({ id }) => [
        this.gridService.filters$(id),
        this.gridService.page$(id),
        this.gridService.rows$(id),
        this.gridService.sortsObject$(id),
      ]),
      exhaustMap(([{ id }, filters, page, rows, sorts]) =>
        this.carrierService.getCarrierLookup({ filters, page, rows, sorts }).pipe(
          map((data) => CarrierActions.CarrierLookupDialogRequestDataSuccess({ id, data })),
          catchError((_error) => of(CarrierActions.CarrierLookupDialogRequestDataFailure({ id })))
        )
      )
    );
  });
}
